import React, { createContext, useContext, useState, useEffect } from 'react';
import { AccessDeniedOverlay } from '../components/AccessDeniedOverlay';
import { useKeycloak } from './KeycloakContext';

interface AccessDeniedInfo {
  reason: string;
  currentTime?: string;
}

interface AccessDeniedContextType {
  showAccessDenied: (info: AccessDeniedInfo) => void;
  hideAccessDenied: () => void;
}

const AccessDeniedContext = createContext<AccessDeniedContextType>({
  showAccessDenied: () => {},
  hideAccessDenied: () => {},
});

export const useAccessDenied = () => useContext(AccessDeniedContext);

export const AccessDeniedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [deniedInfo, setDeniedInfo] = useState<AccessDeniedInfo | null>(null);
  const { logout } = useKeycloak();

  const showAccessDenied = (info: AccessDeniedInfo) => {
    setDeniedInfo(info);
    setIsVisible(true);
  };

  const hideAccessDenied = () => {
    setIsVisible(false);
    setDeniedInfo(null);
  };

  const handleLogout = () => {
    hideAccessDenied();
    logout();
  };

  // Listen for access denied events from API interceptor
  useEffect(() => {
    const handleAccessDeniedEvent = (event: CustomEvent) => {
      const { reason, currentTime } = event.detail;
      showAccessDenied({ reason, currentTime });
    };

    window.addEventListener('showAccessDenied', handleAccessDeniedEvent as EventListener);

    return () => {
      window.removeEventListener('showAccessDenied', handleAccessDeniedEvent as EventListener);
    };
  }, []);

  return (
    <AccessDeniedContext.Provider value={{ showAccessDenied, hideAccessDenied }}>
      {children}
      {isVisible && deniedInfo && (
        <AccessDeniedOverlay
          reason={deniedInfo.reason}
          currentTime={deniedInfo.currentTime}
          onLogout={handleLogout}
        />
      )}
    </AccessDeniedContext.Provider>
  );
};
